import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Search, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { QuickProductDialog } from "@/components/product/QuickProductDialog";
import { useAuth } from "@/context/AuthContext";
import { api, brl, imgUrl, fmtQty, canDo } from "@/lib/api";
import { cn } from "@/lib/utils";
import { isCode, resolveCode, scaleCfg, useScanner } from "@/lib/barcode";

export function useCatalog() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  useEffect(() => {
    Promise.all([api.get("/products"), api.get("/categories")]).then(([p, c]) => {
      setProducts(p.data.filter((x) => x.active));
      setCategories(c.data);
    });
  }, []);
  return { products, categories, addProduct: (p) => setProducts((ps) => [...ps, p]) };
}

export function ProductPicker({ onPick, compact = false, quickCreate = false, panel = false }) {
  const { products, categories, addProduct } = useCatalog();
  const { session } = useAuth();
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [newCode, setNewCode] = useState(null);
  const closeNew = useCallback(() => setNewCode(null), []);
  const box = useRef(null);

  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return products.filter(
      (p) => (cat === "all" || p.category_id === cat) && (!s || p.name.toLowerCase().includes(s) || (p.barcode || "").includes(s))
    );
  }, [products, cat, q]);

  const byCode = (c, fallback) => {
    const hit = resolveCode(products, c, scaleCfg(session.tenant.settings));
    const pick = hit?.product || fallback;
    if (pick) { onPick(pick, hit?.product ? hit.qty : undefined); setQ(""); return; }
    if (hit) { toast.error(`Etiqueta de balança: nenhum produto com o código de balança ${hit.missing}`); return; }
    if (!isCode(c)) return;
    if (!quickCreate) toast.error(`Nenhum produto com o código ${c}`);
    else if (canDo(session.user, "edit_products")) setNewCode(c);
    else toast.error("Produto não cadastrado. Peça para quem pode editar produtos fazer o cadastro.");
  };
  const onKey = (e) => { if (e.key === "Enter") byCode(q.trim(), list.length === 1 ? list[0] : null); };
  useScanner((c) => byCode(c, null), box);
  const created = (p) => { addProduct(p); onPick(p); setNewCode(null); setQ(""); };
  const cats = [{ id: "all", name: "Todos" }, ...categories];

  const search = (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey} placeholder="Buscar produto ou código de barras (Enter adiciona)"
        className="pl-9 h-11" data-testid="pos-product-search" />
    </div>
  );
  const chips = (
    <div className="flex shrink-0 gap-2 overflow-x-auto pb-1" data-testid="pos-category-tabs">
      {cats.map((c) => (
        <button key={c.id} onClick={() => setCat(c.id)} data-testid={`pos-category-${c.id}`}
          className={cn("shrink-0 rounded-full border px-4 py-2 text-sm font-medium transition-colors touch",
            cat === c.id ? "bg-primary text-primary-foreground border-primary" : "hover:bg-accent")}>
          {c.name}
        </button>
      ))}
    </div>
  );
  const grid = (
    <div className={cn("grid gap-3", compact ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-2 sm:grid-cols-3 xl:grid-cols-4")}>
      {list.map((p) => (
        <button key={p.id} onClick={() => onPick(p)} data-testid={`product-card-${p.id}`}
          className="card-surface card-hover overflow-hidden text-left active:scale-[0.98] transition-transform">
          {!compact && (
            <div className="aspect-[4/3] bg-muted grid place-items-center overflow-hidden">
              {p.image_url ? <img src={imgUrl(p.image_url)} alt={p.name} className="h-full w-full object-cover" loading="lazy" /> : <ImageOff className="h-6 w-6 text-muted-foreground/50" />}
            </div>
          )}
          <div className="p-3">
            <p className="line-clamp-2 text-sm font-semibold leading-snug">{p.name}</p>
            <div className="mt-1 flex items-center justify-between gap-2">
              <span className="font-mono text-sm font-bold text-primary">{brl(p.price)}</span>
              {p.stock != null && <span className={cn("text-[10px]", p.stock <= (p.min_stock || 0) ? "font-semibold text-red-500" : "text-muted-foreground")}>Est. {fmtQty(p.stock)}</span>}
            </div>
            {p.option_groups?.length > 0 && <p className="text-[10px] text-muted-foreground">com opções</p>}
          </div>
        </button>
      ))}
      {!list.length && <p className="col-span-full py-8 text-center text-sm text-muted-foreground">Nenhum produto encontrado.{quickCreate && isCode(q.trim()) && " Tecle Enter para cadastrar este código."}</p>}
    </div>
  );
  const rows = (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-3" data-testid="order-product-grid">
      {list.map((p) => (
        <button key={p.id} onClick={() => onPick(p)} data-testid={`product-card-${p.id}`}
          className="flex min-h-[96px] items-center gap-3 rounded-md border bg-card p-2.5 text-left transition-[border-color,transform] hover:border-primary active:scale-[0.98]">
          {p.image_url && <img src={imgUrl(p.image_url)} alt={p.name} className="h-20 w-20 shrink-0 rounded object-cover" loading="lazy" />}
          <span className="min-w-0 flex-1">
            <span className="line-clamp-2 text-sm font-medium leading-snug">{p.name}</span>
            <span className="mt-2 block text-sm text-muted-foreground">{brl(p.price)}</span>
            {p.option_groups?.length > 0 && <span className="text-[10px] text-muted-foreground">com opções</span>}
          </span>
        </button>
      ))}
      {!list.length && <p className="col-span-full py-8 text-center text-sm text-muted-foreground">Nenhum produto encontrado.</p>}
    </div>
  );
  const quick = newCode && <QuickProductDialog code={newCode} categories={categories} onClose={closeNew} onCreated={created} />;

  if (panel) {
    return (
      <div ref={box} className="grid h-full min-h-0 lg:grid-cols-[250px_minmax(0,1fr)]">
        <aside className="hidden min-h-0 flex-col border-r lg:flex" data-testid="order-category-list">
          <p className="flex h-16 shrink-0 items-center border-b px-5 text-lg font-semibold">Categorias</p>
          <nav className="flex-1 overflow-y-auto">
            {cats.map((c) => (
              <button key={c.id} onClick={() => setCat(c.id)} data-testid={`order-category-${c.id}`}
                className={cn("block w-full border-b px-5 py-5 text-left text-sm transition-colors", cat === c.id ? "bg-muted font-semibold" : "text-muted-foreground hover:bg-accent hover:text-foreground")}>
                {c.name}
              </button>
            ))}
          </nav>
        </aside>
        <div className="flex min-h-0 min-w-0 flex-col">
          <label className="flex h-16 shrink-0 items-center gap-3 border-b px-5">
            <Search className="h-5 w-5 shrink-0 text-muted-foreground" />
            <input id="order-product-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey} placeholder="Encontre produtos por nome ou código (Ctrl + B)"
              className="h-full min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground" data-testid="pos-product-search" />
          </label>
          <div className="border-b px-4 py-3 lg:hidden">{chips}</div>
          <div className="min-h-0 flex-1 overflow-y-auto p-4">{rows}</div>
        </div>
        {quick}
      </div>
    );
  }
  return (
    <div ref={box} className="flex flex-col gap-3 min-h-0">
      {search}
      {chips}
      {grid}
      {quick}
    </div>
  );
}
